import { Typography, Card, Row, Col, Badge, Progress, Space, Tag, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getDetailedHealth } from '@/services/health';

interface HealthData {
  database: { status: string; responseTimeMs: number };
  redis: { status: string; responseTimeMs: number };
  memory: { heapUsedMB: number; heapTotalMB: number; rssMB: number };
  uptime: number;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
}

export default function SystemHealthPage() {
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['health'],
    queryFn: getDetailedHealth,
    refetchInterval: 30000,
  });

  const health = (data as { data?: HealthData })?.data ?? (data as HealthData | undefined);

  if (isLoading) return <Spin size="large" />;

  const dbStatus = health?.database?.status === 'up' ? 'success' : 'error';
  const redisStatus = health?.redis?.status === 'up' ? 'success' : 'error';
  const memPercent = health?.memory ? Math.round((health.memory.heapUsedMB / health.memory.heapTotalMB) * 100) : 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space align="center">
          <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>System Health</Typography.Title>
          <Tag color="red" style={{ borderRadius: 20, fontWeight: 700 }}>Super Admin</Tag>
        </Space>
        <div><Typography.Text style={{ color: '#64748b', fontSize: 13 }}>Real-time system health monitoring (refreshes every 30s)</Typography.Text></div>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card title="API Server"><Badge status="success" text="Operational" /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card title="Database">
            <Badge status={dbStatus as 'success' | 'error'} text={health?.database?.status === 'up' ? `Up (${health.database.responseTimeMs}ms)` : 'Down'} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card title="Redis">
            <Badge status={redisStatus as 'success' | 'error'} text={health?.redis?.status === 'up' ? `Up (${health.redis.responseTimeMs}ms)` : 'Down'} />
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12}>
          <Card title="Memory Usage">
            <Progress percent={memPercent} status={memPercent > 80 ? 'exception' : 'active'} />
            <Typography.Text type="secondary">
              {health?.memory?.heapUsedMB ?? 0}MB / {health?.memory?.heapTotalMB ?? 0}MB (RSS: {health?.memory?.rssMB ?? 0}MB)
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="Uptime">
            <Typography.Title level={4}>{health?.uptime ? formatUptime(health.uptime) : 'N/A'}</Typography.Title>
          </Card>
        </Col>
      </Row>
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
        Last checked: {dataUpdatedAt ? dayjs(dataUpdatedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A'} (auto-refreshes every 30s)
      </Typography.Text>
    </div>
  );
}
